import React from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Header from 'components/Header/Header.js';
import HeaderLinks from 'components/Header/HeaderLinks.js';
import HomeFooter from 'components/Footer/HomeFooter.js';
import WebsiteMeta from './WebsiteMeta.jsx';
import { getPartnerWebsiteContent, getWebsiteContent } from '../common/websiteUtils';
import { getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { buildPartnerNavigation } from '../common/navigationUtils';

export default function PublicLayout({ children, title, description, image, partner, homePath, bookingPath }) {
  const settings = useSelector((state) => state.settingsdata.settings);
  const { i18n } = useTranslation();

  const baseWebsiteContent = partner
    ? getPartnerWebsiteContent(settings, partner, i18n.language)
    : getWebsiteContent(settings, i18n.language);
  const previewTemplate =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('previewTemplate') : null;
  const templateKey = previewTemplate
    ? normalizeTemplateKey(previewTemplate)
    : normalizeTemplateKey(baseWebsiteContent.template || 'classic');
  const siteTheme = getSiteTheme(templateKey, baseWebsiteContent.appearance || {});

  const headerColor = siteTheme.headerColor || 'dark';
  const headerScrollColor = siteTheme.headerScrollColor || headerColor;
  const navMode = siteTheme.navMode || (headerColor === 'white' ? 'light' : 'dark');

  const resolvedHome = homePath || '/';
  const resolvedBooking = bookingPath || (partner ? '/book' : '/addbookings');
  const navigation =
    buildPartnerNavigation(baseWebsiteContent.navigation || {}, {
      homePath: resolvedHome,
      bookingPath: resolvedBooking,
    }) || {};
  const seo = baseWebsiteContent.seo || {};
  const metaTitle = title || seo.title || settings?.CompanyName || 'Plukk';
  const metaDescription = description || seo.description;
  const metaImage = image || seo.ogImage;
  const brandName = baseWebsiteContent.branding?.brandName || settings?.CompanyName || settings?.appName;
  const logoUrl = baseWebsiteContent.branding?.logoUrl;

  return (
    <Box sx={{ backgroundColor: siteTheme.pageBg || '#fff' }}>
      <WebsiteMeta
        title={metaTitle}
        description={metaDescription}
        image={metaImage}
        favicon={baseWebsiteContent.branding?.faviconUrl || settings?.favicon || ''}
      />

      <Header
        color={headerColor}
        navMode={navMode}
        routes={[]}
        rightLinks={<HeaderLinks homePath={resolvedHome} bookingPath={resolvedBooking} navigation={navigation} />}
        brandName={brandName}
        logoUrl={logoUrl}
        brandHref={resolvedHome}
        fixed
        changeColorOnScroll={{
          height: 400,
          color: headerScrollColor,
        }}
      />

      <Box component="main">{children}</Box>

      <HomeFooter navigation={navigation} brandName={brandName} theme={siteTheme} />
    </Box>
  );
}
